import { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { ClipboardList, ExternalLink, UserMinus, UserPlus } from 'lucide-react';
import type { SopAsset, SopField } from '@/api/docs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

/**
 * IT Glue "SOP On/Offboarding" records (server/docs/sop.ts): a compact list (ticket card, org page)
 * and a viewer dialog. Fields arrive normalised and password-free; Textbox HTML is sanitised here
 * and rendered on white like the document viewer (IT Glue content carries light-theme styles).
 */

const SANITIZE: Parameters<typeof DOMPurify.sanitize>[1] = { ADD_ATTR: ['target'], FORBID_TAGS: ['style', 'form', 'input', 'button'] };
const HTML_CLS = 'rounded-md bg-white p-3 text-sm leading-relaxed text-neutral-900 [&_a]:text-blue-700 [&_a]:underline [&_img]:max-w-full [&_li]:ml-5 [&_ol]:list-decimal [&_p]:my-1 [&_ul]:list-disc [&_table]:my-2 [&_td]:border [&_td]:border-neutral-300 [&_td]:px-2 [&_td]:py-1';

const KIND_ICON = { onboarding: UserPlus, offboarding: UserMinus, other: ClipboardList } as const;

/** One line per record: type badge + location (when tagged). */
export function SopList({ items, onOpen, compact = false }: { items: SopAsset[]; onOpen: (s: SopAsset) => void; compact?: boolean }) {
  return (
    <ul className="space-y-0.5">
      {items.map((s) => {
        const Icon = KIND_ICON[s.kind];
        const location = s.fields.find((f) => f.kind === 'tags' && /location/i.test(f.label))?.tags?.map((t) => t.name).join(', ');
        return (
          <li key={s.id}>
            <button type="button" onClick={() => onOpen(s)} title={s.updatedAt ? `Updated ${fmtRelative(s.updatedAt)}` : undefined}
              className={cn('flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-muted/40', compact ? 'text-xs' : 'text-sm')}>
              <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="shrink-0 text-foreground">{s.typeLabel ?? s.name}</span>
              {location && <span className="truncate text-muted-foreground">· {location}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function SopViewer({ sop, onClose }: { sop: SopAsset | null; onClose: () => void }) {
  return (
    <Dialog open={!!sop} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[88vh] w-[min(860px,94vw)] max-w-none flex-col gap-0 p-0">
        {sop && (
          <>
            <DialogHeader className="border-b border-divider px-5 py-3">
              <DialogTitle className="flex items-center gap-2 pr-6 text-base">
                <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{sop.typeLabel ? `${sop.typeLabel} SOP` : sop.name}</span>
              </DialogTitle>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {sop.updatedAt && <span>Updated {fmtRelative(sop.updatedAt)}</span>}
                {sop.url && <a href={sop.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground hover:underline">Open in IT Glue <ExternalLink className="h-3 w-3" /></a>}
                <span>Password fields are never shown here.</span>
              </div>
            </DialogHeader>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-5 py-4">
              {sop.fields.length === 0 && <p className="text-sm text-muted-foreground">This SOP has no content filled in.</p>}
              {sop.fields.map((f, i) => <FieldRow key={`${i}:${f.label}`} field={f} />)}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ field: f }: { field: SopField }) {
  const html = useMemo(() => (f.kind === 'html' && f.value
    ? (DOMPurify.sanitize(f.value, SANITIZE) as unknown as string).replace(/<a /g, '<a target="_blank" rel="noreferrer" ')
    : ''), [f]);
  if (f.kind === 'header') {
    return <h3 className="pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:pt-0">{f.label}</h3>;
  }
  if (f.kind === 'html') {
    return (
      <div className="py-1.5">
        <div className="mb-1 text-xs font-medium text-muted-foreground">{f.label}</div>
        <div className={HTML_CLS} dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[200px_1fr] gap-3 border-b border-divider py-1.5 text-sm last:border-b-0">
      <div className="text-muted-foreground">{f.label}</div>
      <div className="min-w-0 text-foreground">
        {f.kind === 'tags'
          ? f.tags!.map((t, i) => (
            <span key={`${t.name}${i}`}>
              {i > 0 && ', '}
              {t.url ? <a href={t.url} target="_blank" rel="noreferrer" className="hover:underline">{t.name}</a> : t.name}
            </span>
          ))
          : <span className="whitespace-pre-line">{f.value}</span>}
      </div>
    </div>
  );
}

/** "Onboarding SOP" / "Offboarding SOP" heading text for a ticket kind. */
export const sopHeading = (kind: SopAsset['kind']) => (kind === 'offboarding' ? 'Offboarding SOP' : kind === 'onboarding' ? 'Onboarding SOP' : 'On/Offboarding SOP');

export function useSopViewer() {
  const [open, setOpen] = useState<SopAsset | null>(null);
  return { open, setOpen, viewer: <SopViewer sop={open} onClose={() => setOpen(null)} /> };
}
